import * as constants from './appConsts'
import moment from 'moment'

export const getFormattedDate = value => {
  return `${moment(new Date(value)).format(constants.LONG_DATE_FORMAT)}`
}

export const getDateFormatted = (
  value,
  format = constants.LONG_DATE_FORMAT,
) => {
  return `${moment(new Date(value)).format(format)}`
}

export const getFormattedAPIDate = value => {
  return `${moment(new Date(value)).format(constants.API_DATE_FORMAT)}`
}

export const getFormattedDateTime = value => {
  return `${moment(new Date(value)).format(constants.LONG_DATE_TIME_FORMAT)}`
}

export const getAscFormattedDateTime = value => {
  return `${moment(new Date(value)).format(
    constants.ASC_LONG_DATE_TIME_FORMAT,
  )}`
}

export const getFormattedUTCDateTime = value => {
  return `${moment
    .utc(new Date(value))
    .format(constants.LONG_DATE_TIME_FORMAT)}`
}

export const getFormattedUTCDate = value => {
  return `${moment.utc(new Date(value)).format(constants.LONG_DATE_FORMAT)}`
}

export const getUTCDateTime = value => {
  return moment.utc(new Date(value))
}

export const getNewFormatUTCDateTime = (
  value,
  includeTimezone = false,
  delimiter = ' | ',
) => {
  return `${moment
    .utc(value)
    .format(
      constants.LONG_DATE_FORMAT + delimiter + constants.LONG_TIME_FORMAT,
    )}${includeTimezone ? ' GMT' : ''}`
}

export const getDateTime = value => {
  return new Date(moment(value))
}

export const getCurrentUTCTimeStamp = () => {
  return moment.utc().valueOf()
}

export const getDateGivenOffset = (value, offset) => {
  return new Date(moment(new Date(value)).add(offset, 'days'))
}

export const getFormattedDateGivenOffset = (value, offset) => {
  return `${moment(new Date(value))
    .add(offset, 'days')
    .format(constants.LONG_DATE_FORMAT)}`
}

export const isDate1AndDate2LessThanDays = (date1, date2, days) => {
  const momentDate1 = moment(new Date(date1))
  const momentDate2 = moment(new Date(date2))
  const diff = Math.abs(momentDate1.diff(momentDate2, 'days'))
  return diff < days
}

export const daysDifference = (date1, date2) => {
  const momentDate1 = moment(new Date(date1))
  const momentDate2 = moment(new Date(date2))
  const diff = momentDate2.diff(momentDate1, 'days')
  return diff
}

const isValidDateSelectorValue = value => {
  const item = constants.DATE_SELECTOR_OPTIONS.titles[value]
  return item !== undefined
}

const getDateSelectorTitle = value => {
  if (isValidDateSelectorValue(value)) {
    const title = constants.DATE_SELECTOR_OPTIONS.titles[value]
    return title
  }

  throw `${value} is not a valid date selector value!`
}

const getDateSelectorStartDateOffset = value => {
  if (isValidDateSelectorValue(value)) {
    const offset = constants.DATE_SELECTOR_OPTIONS.startDateOffsets[value]
    return offset
  }

  throw `${value} is not a valid date selector value!`
}

const getDateSelectorEndDateOffset = value => {
  if (isValidDateSelectorValue(value)) {
    const offset = constants.DATE_SELECTOR_OPTIONS.endDateOffsets[value]
    return offset
  }

  throw `${value} is not a valid date selector value!`
}

const getDateSelectorDateFormat = value => {
  if (isValidDateSelectorValue(value)) {
    const format = constants.DATE_SELECTOR_OPTIONS.dateFormats[value]
    return format
  }

  throw `${value} is not a valid date selector value!`
}

export const getDateSelectorFormattedTitle = (
  value,
  startDate,
  endDate,
  floatFilter,
) => {
  if (floatFilter && value === '0') {
    return ''
  }

  if (isValidDateSelectorValue(value)) {
    const title = getDateSelectorTitle(value)
    const startDateFormatted = getDateSelectorFormattedStartDate(
      value,
      startDate,
    )

    const endDateFormatted = getDateSelectorFormattedEndDate(value, endDate)

    if (value === '5' || value === '6') {
      return `${startDateFormatted}${endDateFormatted}`
    }

    return `${title}`
  }

  return ''
}

const getDateSelectorFormattedStartDate = (value, startDate) => {
  const startDateOffset = getDateSelectorStartDateOffset(value)

  return typeof startDateOffset === 'number'
    ? getFormattedDateGivenOffset(new Date(), startDateOffset)
    : getFormattedDate(startDate)
}

export const getDateSelectorStartDateGivenOffset = (value, startDate) => {
  const startDateOffset = getDateSelectorStartDateOffset(value)

  return typeof startDateOffset === 'number'
    ? getDateGivenOffset(new Date(), startDateOffset)
    : startDate
}

const getDateSelectorFormattedEndDate = (value, endDate) => {
  if (value === '0') {
    return ''
  }
  const endDateOffset = getDateSelectorEndDateOffset(value)
  const dateFormat = getDateSelectorDateFormat(value)

  if (dateFormat === 'start-end') {
    return typeof startDateOffset === 'number'
      ? `-${getFormattedDateGivenOffset(new Date(), endDateOffset)}`
      : `-${getFormattedDate(endDate)}`
  }

  return ''
}

export const getDateSelectorEndDateGivenOffset = (value, endDate) => {
  if (value === '0') {
    return 0
  }
  const endDateOffset = getDateSelectorEndDateOffset(value)
  const dateFormat = getDateSelectorDateFormat(value)
  if (dateFormat === 'start-end') {
    return typeof endDateOffset === 'number'
      ? getDateGivenOffset(new Date(), endDateOffset)
      : endDate
  }

  return 0
}

export const getReportExportMinDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() - 14)
  return new Date(date)
}

export const getReportExportMaxDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() + 1)
  return new Date(date)
}

export const getReportListMinDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() - 90)
  return new Date(date)
}

export const getReportListMinDateGivenOffset = offset => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() - offset)
  return new Date(date)
}

export const getReportListMaxDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() + 1)
  return new Date(date)
}

export const getReportListInitialStartDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() - 14)
  return new Date(date)
}

export const getReportListInitialEndDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() + 1)
  return new Date(date)
}

export const getAnalyticWatchlistInitialStartDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() - 60)
  return new Date(date)
}

export const getAnalyticWatchlistInitialEndDate = () => {
  const today = new Date()
  const date = new Date().setDate(today.getDate() + 1)
  return new Date(date)
}

export const getDateSelectorEndDate = (value, endDate) => {
  if (value === '0') {
    return null
  }
  const endDateOffset = getDateSelectorEndDateOffset(value)

  return typeof endDateOffset === 'number'
    ? getDateGivenOffset(new Date(), endDateOffset)
    : getDateGivenOffset(endDate, 0)
}

export const getDateComparator = (value, startDate, endDate, cellDate) => {
  if (value === '0') {
    return 1
  }

  const startOnlyDate = getOnlyDate(startDate).getTime()
  const endOnlyDate = getOnlyDate(endDate).getTime()
  const cellOnlyDate = getOnlyDate(cellDate).getTime()

  if (value === '1' || value === '2' || value === '5') {
    return startOnlyDate === cellOnlyDate
  }

  return cellOnlyDate >= startOnlyDate && cellOnlyDate <= endOnlyDate
}

export const getOnlyDate = date => {
  const cellDate = new Date(date)
  const cellMonth = cellDate.getMonth() + 1 // months from 1-12
  const cellDay = cellDate.getDate()
  const cellYear = cellDate.getFullYear()
  return new Date(cellYear, cellMonth, cellDay)
}

export const randomDate = (from, to) => {
  const fromTime = from.getTime()
  const toTime = to.getTime()
  return new Date(fromTime + Math.random() * (toTime - fromTime))
}

export const startDateGivenTimeline = timeline => {
  switch (timeline) {
    case '1w':
      return `${moment(new Date())
        .subtract(7, 'days')
        .format(constants.API_DATE_FORMAT)}`
    case '2w':
      return `${moment(new Date())
        .subtract(14, 'days')
        .format(constants.API_DATE_FORMAT)}`
    case '1m':
      return `${moment(new Date())
        .subtract(30, 'days')
        .format(constants.API_DATE_FORMAT)}`
    case '3m':
      return `${moment(new Date())
        .subtract(90, 'days')
        .format(constants.API_DATE_FORMAT)}`
    case '6m':
      return `${moment(new Date())
        .subtract(180, 'days')
        .format(constants.API_DATE_FORMAT)}`
    case '1y':
      return `${moment(new Date())
        .subtract(365, 'days')
        .format(constants.API_DATE_FORMAT)}`
  }
}

export const getStartDateGivenEndDateAndTimeline = (endDate, timeline) => {
  return `${moment(endDate)
    .subtract(timeline, 'days')
    .format(constants.API_DATE_FORMAT)}`
}

export const endDateGivenTimeline = () => {
  return `${moment(new Date()).format(constants.API_DATE_FORMAT)}`
}
